'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHubNewsList } from '@/hooks/feature/hubs/useHubNewsList';
import { useHubNewsSearchParams } from '@/hooks/feature/hubs/useHubNewsSearchParams';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import { HubNewsTagSelection } from '@/types/hubs';
import ImageAdaptive from '@/components/ImageAdaptive';
import ServiceHeader from '@/components/layout/service/ServiceHeader';
import HubNewsHubView from '@/(routers)/hubs/cases/components/HubNewsHubView';
import HubNewsList from '@/(routers)/hubs/cases/components/HubNewsList';
import HubNewsSearch from '@/(routers)/hubs/cases/components/HubNewsSearch';
import HubNewsSearchDetail from '@/(routers)/hubs/cases/components/HubNewsSearchDetail';

const VIEW_COUNT_STEP = 24;

const matchesCategory = (tagCds: string[], categoryCd: string, selection: HubNewsTagSelection) => {
    const selectedTagCds = selection[categoryCd] ?? [];
    if (selectedTagCds.length === 0) return true;
    return selectedTagCds.some((tagCd) => tagCds.includes(tagCd));
};

export default function HubNews() {
    const { hubNewsList, tagCategoryList, isLoading } = useHubNewsList();
    const { openModalSlide, closeModalSlide } = useModalSlideStore();
    const { queryKey, selection, selectedHubIndex, setSelection, toggleTag, clearSelection, clearHub } =
        useHubNewsSearchParams(tagCategoryList);
    const [viewCount, setViewCount] = useState(VIEW_COUNT_STEP);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);

    const hubNewsCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        hubNewsList.forEach((hubNews) => {
            counts[hubNews.hub_index] = (counts[hubNews.hub_index] ?? 0) + 1;
        });
        return counts;
    }, [hubNewsList]);

    const calcResult = useCallback(
        (target: HubNewsTagSelection) => {
            const newsList = hubNewsList.filter((hubNews) =>
                tagCategoryList.every((category) => matchesCategory(hubNews.tag_cds, category.cd, target)),
            );

            const counts: Record<string, number> = {};
            tagCategoryList.forEach((category) => {
                const scoped = hubNewsList.filter((hubNews) =>
                    tagCategoryList.every(
                        (other) => other.cd === category.cd || matchesCategory(hubNews.tag_cds, other.cd, target),
                    ),
                );
                category.tags.forEach((tag) => {
                    counts[tag.cd] = scoped.filter((hubNews) => hubNews.tag_cds.includes(tag.cd)).length;
                });
            });

            return { newsList, tagCounts: counts };
        },
        [hubNewsList, tagCategoryList],
    );

    const { newsList: filteredNewsList, tagCounts } = useMemo(() => calcResult(selection), [calcResult, selection]);

    const baseTagCounts = useMemo(() => calcResult({}).tagCounts, [calcResult]);

    const hasTaggedNews = useMemo(() => Object.values(baseTagCounts).some((count) => count > 0), [baseTagCounts]);

    const hubNewsListOfHub = useMemo(
        () => hubNewsList.filter((hubNews) => hubNews.hub_index === selectedHubIndex),
        [hubNewsList, selectedHubIndex],
    );

    const selectedHub = hubNewsListOfHub.length > 0 ? hubNewsListOfHub[0] : null;
    const hasMore = viewCount < filteredNewsList.length;

    const selectedCount = useMemo(
        () => Object.values(selection).reduce((total, tagCds) => total + tagCds.length, 0),
        [selection],
    );

    useEffect(() => {
        setViewCount(VIEW_COUNT_STEP);
    }, [queryKey]);

    useEffect(() => {
        if (!loadMoreRef.current || !hasMore) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setViewCount((prev) => prev + VIEW_COUNT_STEP);
                }
            },
            { threshold: 1 },
        );
        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [hasMore, viewCount]);

    const handleSelectHub = (hubIndex: string) => {
        const newsListOfHub = hubNewsList.filter((hubNews) => hubNews.hub_index === hubIndex);
        if (newsListOfHub.length === 0) return;
        openModalSlide({
            id: 'hub-news-hub',
            direction: 'right',
            size: { pcWidth: '680px', mbWidth: '100%', height: '100%' },
            component: <HubNewsHubView hubNews={newsListOfHub[0]} hubNewsList={newsListOfHub} variant="slide" />,
        });
    };

    const handleOpenDetail = () => {
        openModalSlide({
            id: 'hub-news-search-detail',
            direction: 'right',
            size: { pcWidth: '540px', mbWidth: '100%', height: '100%' },
            component: (
                <HubNewsSearchDetail
                    categories={tagCategoryList}
                    selection={selection}
                    hasTaggedNews={hasTaggedNews}
                    calcResult={calcResult}
                    onApply={setSelection}
                    onClose={closeModalSlide}
                />
            ),
        });
    };

    return (
        <>
            <ServiceHeader title="Case." breadcrumbLeaf={selectedHub?.hub_name} />

            {selectedHub ? (
                <HubNewsHubView hubNews={selectedHub} hubNewsList={hubNewsListOfHub} variant="page" onBack={clearHub} />
            ) : (
                <>
                    {hubNewsList.length > 0 && (
                        <div className="flex justify-center w-full py-2">
                            <div className="flex flex-col w-full sm:w-[98%] rounded-none sm:rounded-xl bg-color-white-base px-2 sm:px-8 pt-4 pb-6">
                                <HubNewsSearch
                                    categories={tagCategoryList}
                                    selection={selection}
                                    tagCounts={tagCounts}
                                    baseTagCounts={baseTagCounts}
                                    hasTaggedNews={hasTaggedNews}
                                    resultCount={filteredNewsList.length}
                                    selectedCount={selectedCount}
                                    copyUrl={`/hubs/cases?${queryKey}`}
                                    onToggleTag={toggleTag}
                                    onClear={clearSelection}
                                    onOpenDetail={handleOpenDetail}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex justify-center w-full py-4">
                        <div className="flex flex-col w-full sm:w-[98%]">
                            {isLoading ? (
                                <div className="flex justify-center items-center w-full my-20">
                                    <ImageAdaptive
                                        src="/images/common/loading-circle.svg"
                                        baseWidth={80}
                                        breakpoints={{ md: 200 }}
                                    />
                                </div>
                            ) : (
                                <div className="flex justify-center items-center w-full py-4">
                                    <HubNewsList
                                        hubNewsList={filteredNewsList.slice(0, viewCount)}
                                        hubNewsCounts={hubNewsCounts}
                                        onSelectHub={handleSelectHub}
                                    />
                                </div>
                            )}
                            <div ref={loadMoreRef} style={{ height: '1px' }} />
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
